import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import strings from "../i18n/language";
import {
  addSpecialistAvailabilityAction,
  deleteSpecialistAvailabilityAction,
} from "../redux/actions/specialist";

const TIME_OPTIONS = (() => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    options.push(`${String(h).padStart(2, "0")}:00`);
    options.push(`${String(h).padStart(2, "0")}:30`);
  }
  return options;
})();

const DAY_NAMES = [
  strings.DAY_MONDAY,
  strings.DAY_TUESDAY,
  strings.DAY_WEDNESDAY,
  strings.DAY_THURSDAY,
  strings.DAY_FRIDAY,
  strings.DAY_SATURDAY,
  strings.DAY_SUNDAY,
];

const SpecialistAvailabilities = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const specialist = useSelector((state) => state.specialist.specialist);
  const isLoadingAvailability = useSelector(
    (state) => state.specialist.isLoadingAvailability
  );
  const user = useSelector((state) => state.user.user);

  const [newDay, setNewDay] = useState(0);
  const [newStart, setNewStart] = useState("08:00");
  const [newEnd, setNewEnd] = useState("12:00");

  if (!specialist) return null;

  const availabilities = specialist.UserAvailabilities || [];

  const slotsByDay = DAY_NAMES.reduce((acc, _, dayIndex) => {
    acc[dayIndex] = availabilities
      .filter((a) => a.dayOfWeek === dayIndex)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    return acc;
  }, {});

  const handleAdd = () => {
    if (newEnd <= newStart) return;
    dispatch(
      addSpecialistAvailabilityAction(specialist.id, {
        dayOfWeek: newDay,
        startTime: newStart,
        endTime: newEnd,
      })
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {strings.TITLE_AVAILABILITIES} — {specialist.name}
        <Typography variant="body2" color="text.secondary">
          {specialist.specialty}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {user.type !== "viewer" && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <FormControl variant="filled" sx={{ minWidth: 130 }}>
                <InputLabel>{strings.LABEL_DAY}</InputLabel>
                <Select
                  value={newDay}
                  onChange={(e) => setNewDay(e.target.value)}
                >
                  {DAY_NAMES.map((day, i) => (
                    <MenuItem key={i} value={i}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="filled" sx={{ minWidth: 110 }}>
                <InputLabel>{strings.LABEL_FROM}</InputLabel>
                <Select
                  value={newStart}
                  onChange={(e) => setNewStart(e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="filled" sx={{ minWidth: 110 }}>
                <InputLabel>{strings.LABEL_TO}</InputLabel>
                <Select
                  value={newEnd}
                  onChange={(e) => setNewEnd(e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                color="primary"
                onClick={handleAdd}
                disabled={isLoadingAvailability || newEnd <= newStart}
              >
                {isLoadingAvailability ? (
                  <CircularProgress size={20} />
                ) : (
                  <AddIcon />
                )}
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {availabilities.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {strings.MESSAGE_NO_AVAILABILITIES}
          </Typography>
        )}

        {DAY_NAMES.map((dayName, dayIndex) => {
          const slots = slotsByDay[dayIndex];
          if (!slots || slots.length === 0) return null;
          return (
            <Box key={dayIndex} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                {dayName}
              </Typography>
              {slots.map((slot) => (
                <Box
                  key={slot.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pl: 1,
                    py: 0.25,
                  }}
                >
                  <Typography variant="body2">
                    {slot.startTime.substring(0, 5)} –{" "}
                    {slot.endTime.substring(0, 5)}
                  </Typography>
                  {user.type !== "viewer" && (
                    <IconButton
                      size="small"
                      disabled={isLoadingAvailability}
                      onClick={() =>
                        dispatch(
                          deleteSpecialistAvailabilityAction(
                            specialist.id,
                            slot.id
                          )
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Divider sx={{ mt: 1 }} />
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{strings.BUTTON_CLOSE}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialistAvailabilities;
